import { memo, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  AudioWaveformIcon,
  CheckIcon,
  CloseIcon,
  CopyIcon,
  CursorIcon,
  DownloadIcon,
  FileTextIcon,
  PauseIcon,
  PlayIcon,
  MicrophoneIcon,
  SettingsIcon,
  TranslateIcon,
} from "@/components/Icons";
import type { Persona } from "@/models/onboardingPersona";
import { LANGUAGES } from "@/languages";
import { MAX_IMPORT_FILES } from "@/file-picker";
import DictationBubbleIcon from "@/components/DictationBubbleIcon";

type Workflow = "upload" | "record" | "dictate";
type PreviewPlatform = "macos" | "windows";
type DemoText = (key: string) => string;
type Permission = "microphone" | "systemAudio" | "accessibility";

// Inert, localized stories: watching cannot upload, record, or grant permission.
// React advances only between scenes. CSS owns the cursor, key and audio motion.
const workflowDurations: Record<Workflow, readonly number[]> = {
  upload: [1800, 1600, 2800, 3200],
  record: [1800, 3000, 1800, 3400],
  dictate: [1600, 3400, 1400, 3200],
};
const PreviewPlayer = ({
  labels,
  children,
  note,
  sceneDurationMs = 2000,
  durations,
  onComplete,
}: {
  labels: string[];
  children: (scene: number) => ReactNode;
  note?: string;
  sceneDurationMs?: number;
  durations?: readonly number[];
  onComplete?: () => void;
}) => {
  const { t } = useTranslation();
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(
    () => !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [visible, setVisible] = useState(() => !document.hidden);
  const running = playing && visible;
  const last = labels.length - 1;
  const duration = durations?.[scene] ?? sceneDurationMs;
  useEffect(() => {
    if (!running) return;
    const timer = window.setTimeout(() => {
      // Reduced motion is opt-in playback, once; ordinary demos keep looping.
      if (
        scene === last &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      )
        setPlaying(false);
      else if (scene === last && onComplete) onComplete();
      else setScene((value) => (value === last ? 0 : value + 1));
    }, duration);
    return () => window.clearTimeout(timer);
  }, [running, scene, last, duration, onComplete]);
  useEffect(() => {
    const pause = () => setVisible(false);
    const resume = () => setVisible(!document.hidden);
    const visibility = () => setVisible(!document.hidden);
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const reduce = () => {
      if (media.matches) setPlaying(false);
    };
    // action-id: lifecycle.dictation.desktopDemoVisibility
    document.addEventListener("visibilitychange", visibility);
    // action-id: lifecycle.dictation.desktopDemoVisibility
    window.addEventListener("blur", pause);
    // action-id: lifecycle.dictation.desktopDemoVisibility
    window.addEventListener("focus", resume);
    // action-id: lifecycle.dictation.desktopDemoVisibility
    media.addEventListener("change", reduce);
    return () => {
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("blur", pause);
      window.removeEventListener("focus", resume);
      media.removeEventListener("change", reduce);
    };
  }, []);
  return (
    <div
      data-testid="desktop-demo-player"
      data-scene={scene}
      data-scene-count={labels.length}
      data-scene-duration={duration}
      data-playing={running}
      className="min-w-0"
    >
      <div className="mb-2 flex items-center justify-between gap-3 text-[11px] text-[var(--text-tertiary)]">
        <span>{t("desktopSetup.preview.label")}</span>
        <span className="ms-auto">
          {note ?? t("desktopSetup.preview.sample")}
        </span>
        <button
          type="button"
          data-action-id="dictation.setup.demoPlayback"
          aria-label={t(
            playing
              ? "desktopSetup.preview.pause"
              : "alertWidgetCreateTranscriptVoiceRecording.resume",
          )}
          onClick={() => {
            if (!playing && scene === last) setScene(0);
            setPlaying((value) => !value);
          }}
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-[var(--text-secondary)] hover:bg-[var(--fill-1)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
        >
          {playing ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
        </button>
      </div>
      <div
        aria-hidden="true"
        className="desktop-lesson-stage glass-tile relative isolate overflow-hidden rounded-[24px] text-[var(--text-primary)] shadow-xl shadow-black/5"
        data-playing={running}
      >
        <div className="desktop-lesson-enter h-full">{children(scene)}</div>
      </div>
      <p
        data-testid="desktop-demo-caption"
        aria-live={running ? "off" : "polite"}
        aria-atomic="true"
        className="sr-only"
      >
        {labels[scene]}
      </p>
    </div>
  );
};

const Pointer = () => (
  <span className="desktop-lesson-pointer pointer-events-none absolute end-2 top-1/2 text-[var(--text-primary)] drop-shadow-md">
    <CursorIcon size={26} />
  </span>
);
const AudioMeter = () => (
  <span className="flex h-6 items-center gap-1">
    {[9, 18, 24, 14, 21, 11].map((height, i) => (
      <span
        key={i}
        className="desktop-lesson-wave w-0.5 rounded-full bg-current"
        style={{ height, animationDelay: `${i * 70}ms` }}
      />
    ))}
  </span>
);
const WindowBar = ({
  title,
  platform,
}: {
  title: string;
  platform: PreviewPlatform;
}) => (
  <div
    data-testid="desktop-demo-window"
    data-platform={platform}
    dir="ltr"
    className="flex h-9 shrink-0 items-center gap-1.5 bg-[var(--fill-1)] px-4"
  >
    {platform === "macos" && (
      <>
        <span className="size-2 rounded-full bg-[#ff5f57]" />
        <span className="size-2 rounded-full bg-[#febc2e]" />
        <span className="size-2 rounded-full bg-[#28c840]" />
      </>
    )}
    <span
      dir="auto"
      className="ms-2 flex-1 truncate text-[11px] font-medium text-[var(--text-secondary)]"
    >
      {title}
    </span>
    {platform === "windows" && (
      <span className="text-xs tracking-[12px]">— □ ×</span>
    )}
  </div>
);

const UploadFilesPreview = ({
  selected = false,
  sample,
}: {
  selected?: boolean;
  sample: DemoText;
}) => {
  const { t } = useTranslation();
  const files = [
    sample("import_fileName").split("·")[0].trim(),
    `${t("desktopSetup.preview.interviewFile")}.mp3`,
    `${t("desktopSetup.preview.lectureFile")}.mp4`,
    `${t("desktopSetup.preview.voiceNoteFile")}.m4a`,
    `${t("desktopSetup.preview.projectUpdateFile")}.wav`,
  ];
  return (
    <div className="space-y-1">
      {files.map((file) => (
        <div
          key={file}
          data-testid="desktop-demo-upload-file"
          data-selected={selected}
          className={`flex h-8 items-center gap-2.5 rounded-lg px-3 ${selected ? "bg-[var(--fill-2)]" : "bg-[var(--fill-1)]"}`}
        >
          <span className="shrink-0 text-[var(--text-secondary)]">
            <AudioWaveformIcon size={16} />
          </span>
          <span
            dir="auto"
            className="min-w-0 flex-1 truncate text-xs font-medium"
          >
            {file}
          </span>
          {selected && (
            <span className="shrink-0 text-[var(--accent)]">
              <CheckIcon size={14} />
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const ProcessingPreview = ({
  platform,
  sample,
}: {
  platform: PreviewPlatform;
  sample: DemoText;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col">
      <WindowBar title="1Transcribe" platform={platform} />
      <div className="flex min-h-0 flex-1 items-center justify-center px-5 py-3">
        <div className="w-full min-w-0 max-w-md">
          <p className="mb-3 text-sm font-semibold">
            {t("desktopSetup.preview.transcribingFiles", {
              count: MAX_IMPORT_FILES,
            })}
          </p>
          <UploadFilesPreview sample={sample} />
          <div className="mt-4 h-1 overflow-hidden rounded-full bg-[var(--fill-2)]">
            <div className="desktop-lesson-progress h-full bg-[var(--accent)]" />
          </div>
        </div>
      </div>
    </div>
  );
};

const TranscriptPreview = ({
  meeting = false,
  platform,
  download,
  sample,
}: {
  meeting?: boolean;
  platform: PreviewPlatform;
  download?: "choose" | "saved";
  sample: DemoText;
}) => {
  const { t } = useTranslation();
  return (
    <div className="relative flex h-full flex-col">
      <WindowBar title="1Transcribe" platform={platform} />
      <div className="mx-auto flex w-full max-w-xl min-h-0 flex-1 flex-col gap-2 px-4 py-3">
        <div className="flex items-center gap-3">
          <FileTextIcon size={18} />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">
            {sample("import_fileName").split("·")[0]}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="glass flex items-center gap-1.5 rounded-full px-3 py-2">
            <CopyIcon size={13} />
            {t("transcript.copy")}
          </span>
          <span
            data-demo-target={
              download === "choose" ? "download-transcript" : undefined
            }
            className="glass relative flex items-center gap-1.5 rounded-full px-3 py-2"
          >
            <DownloadIcon size={13} />
            {t("transcript.download")}
            {download === "choose" && <Pointer />}
          </span>
          <span className="glass flex items-center gap-1.5 rounded-full px-3 py-2">
            <TranslateIcon size={13} />
            {t("transcript.translate")}
          </span>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-2xl bg-[var(--bg-secondary)] px-4 py-3">
          {[1, 2].map((n) => (
            <div key={n}>
              <p className="mb-1 font-mono text-[10px] text-[var(--text-tertiary)]">
                {sample(`import_speaker${n}`)}
              </p>
              <p className="text-sm leading-relaxed">
                {sample(`import_line${n}`)}
              </p>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 text-[10px] text-[var(--text-secondary)]">
          <span className="flex items-center gap-2">
            <PlayIcon size={13} />
            <AudioWaveformIcon size={24} />
            <span dir="ltr">00:00 / {meeting ? "00:12" : "1:32:00"}</span>
          </span>
          {meeting ? (
            <span>{t("desktopSetup.preview.transcript")}</span>
          ) : (
            <span>PDF · DOCX · TXT</span>
          )}
        </div>
      </div>
      {download === "saved" && (
        <div
          data-testid="desktop-demo-export"
          className="desktop-lesson-enter glass absolute bottom-4 end-4 flex max-w-[calc(100%-32px)] items-center gap-3 rounded-2xl px-5 py-4 shadow-xl"
        >
          <span className="rounded-xl bg-[var(--accent)] p-2.5 text-[var(--text-inverse)]">
            <FileTextIcon size={24} />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">
              {sample("import_fileName")
                .split("·")[0]
                .trim()
                .replace(/\.[^.]+$/, ".docx")}
            </span>
            <span className="mt-1 block text-xs text-[var(--text-secondary)]">
              {t("desktopSetup.preview.documentReady")}
            </span>
          </span>
          <CheckIcon size={18} />
        </div>
      )}
    </div>
  );
};

const FilePickerPreview = ({
  platform,
  sample,
}: {
  platform: PreviewPlatform;
  sample: DemoText;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col bg-[var(--bg-secondary)]">
      <WindowBar
        title={t("alertCreateTranscriptFromFileImport.title")}
        platform={platform}
      />
      <div className="flex min-h-0 flex-1 items-center justify-center px-5 py-3">
        <div className="w-full min-w-0 max-w-md">
          <p className="mb-3 text-sm text-[var(--text-secondary)]">
            {t("desktopSetup.preview.chooseFile", { count: MAX_IMPORT_FILES })}
          </p>
          <UploadFilesPreview selected sample={sample} />
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-between gap-3 px-5 pb-4 text-xs">
        <span className="text-[var(--text-secondary)]">
          {t("desktopSetup.preview.filesSelected", { count: MAX_IMPORT_FILES })}
        </span>
        <span
          data-demo-target="open-file"
          className="relative rounded-lg bg-[var(--accent)] px-5 py-2.5 font-medium text-[var(--text-inverse)]"
        >
          {t("desktopSetup.preview.openFile")}
          <Pointer />
        </span>
      </div>
    </div>
  );
};

// Mirror the shipped vertical bubble (electron pill.css): an 8x40 resting
// sliver, a record satellite revealed on hover, and 30px glass capsules while
// dictating or recording. Not a generic recording toolbar.
const PreviewBubble = ({
  mode,
  finish = false,
}: {
  mode: "idle" | "record" | "dictate" | "working" | "complete";
  finish?: boolean;
}) => {
  const { i18n } = useTranslation();
  const language = (
    LANGUAGES.find((item) => item.value === i18n.resolvedLanguage)?.value ??
    "en"
  ).toUpperCase();
  // Scene 0 of the meeting demo: the pointer hovers the bar and clicks ●.
  const hovering = mode === "idle" && finish;
  return (
    <div
      data-testid="desktop-demo-bubble"
      data-mode={mode}
      className="relative flex flex-col items-center gap-1.5"
    >
      {hovering ? (
        <span
          data-demo-target="start-recording"
          className="desktop-lesson-bubble-satellite relative"
        >
          <span className="desktop-lesson-bubble-recording-dot" />
          <Pointer />
        </span>
      ) : null}
      <div
        className="desktop-lesson-bubble"
        data-mode={mode}
        data-hover={hovering}
        data-finishing={finish}
      >
        {mode === "idle" ? (
          hovering ? (
            <span className="desktop-lesson-bubble-meter" data-idle="true">
              {[0.72, 1, 0.82, 1.12, 0.78].map((shape, index) => (
                <span
                  key={index}
                  style={{ width: Math.round(4 + 4 * shape) }}
                />
              ))}
            </span>
          ) : null
        ) : mode === "complete" ? (
          <CheckIcon size={14} strokeWidth={2.5} />
        ) : mode === "working" ? (
          <span className="flex flex-col items-center gap-1">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="desktop-lesson-wave size-1 rounded-full bg-current"
                style={{ animationDelay: `${dot * 120}ms` }}
              />
            ))}
          </span>
        ) : (
          <>
            <span
              className={
                mode === "record"
                  ? "desktop-lesson-bubble-pause"
                  : "desktop-lesson-bubble-cancel"
              }
            >
              {mode === "record" ? (
                <PauseIcon size={11} />
              ) : (
                <CloseIcon size={11} />
              )}
            </span>
            {mode === "record" ? (
              <span className="desktop-lesson-bubble-recording-status">
                <span className="desktop-lesson-bubble-recording-dot" />
                <span className="desktop-lesson-bubble-time" dir="ltr">
                  {finish ? "0:12" : "0:03"}
                </span>
              </span>
            ) : (
              <span className="desktop-lesson-bubble-meter">
                {[0.72, 1, 0.82, 1.12, 0.78].map((shape, index) => (
                  <span
                    key={index}
                    className="desktop-lesson-wave"
                    style={{
                      width: Math.min(16, 4 + 0.6 * 14 * shape),
                      animationDelay: `${index * 70}ms`,
                    }}
                  />
                ))}
              </span>
            )}
            <span className="desktop-lesson-bubble-language">{language}</span>
            <span
              data-demo-target={
                finish && mode === "record" ? "finish-recording" : undefined
              }
              className="desktop-lesson-bubble-finish relative"
            >
              {mode === "record" ? (
                <span className="desktop-lesson-bubble-stop" />
              ) : (
                <CheckIcon size={11} />
              )}
              {finish && mode === "record" && <Pointer />}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const MeetingPreview = ({
  scene,
  platform,
  sample,
}: {
  scene: number;
  platform: PreviewPlatform;
  sample: DemoText;
}) => {
  const { t } = useTranslation();
  const title = sample("import_fileName")
    .split("·")[0]
    .trim()
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/_/g, " ");
  const speakers = [1, 2].map((n) =>
    sample(`import_speaker${n}`).split("·")[0].trim(),
  );
  return (
    <div className="h-full">
      <div className="desktop-lesson-meeting absolute inset-0 flex flex-col overflow-hidden rounded-b-2xl">
        <WindowBar title={`Microsoft Teams · ${title}`} platform={platform} />
        <div className="flex h-9 shrink-0 items-center gap-3 px-4 text-[10px]">
          <span className="flex size-5 items-center justify-center rounded bg-[#6264a7] font-bold text-white">
            T
          </span>
          <span className="min-w-0 flex-1 truncate font-medium">{title}</span>
          <span className="tabular-nums text-[var(--text-tertiary)]" dir="ltr">
            12:08
          </span>
          <MicrophoneIcon size={14} />
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
          >
            <rect x="2" y="6" width="13" height="12" rx="3" />
            <path d="m15 10 6-3v10l-6-3" />
          </svg>
          <span className="rounded bg-[#c4314b] px-2.5 py-1 text-white">
            {t("desktopSetup.preview.leave")}
          </span>
        </div>
        <div className="flex min-h-0 flex-1 gap-2 px-3 pb-2">
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
            {[...speakers, "Priya", "Alex"].map((name, index) => (
              <div
                key={index}
                data-testid="desktop-demo-participant"
                data-active={index === (scene === 1 ? 1 : 0)}
                className="desktop-lesson-participant relative flex min-h-0 items-center justify-center overflow-hidden rounded-lg"
                data-person={index}
              >
                {index === 3 ? (
                  <span className="flex size-10 items-center justify-center rounded-full bg-white/10 text-sm font-medium">
                    AL
                  </span>
                ) : (
                  <img
                    src="/desktop-meeting-portraits.webp"
                    alt=""
                    width={960}
                    height={320}
                    className="desktop-lesson-portrait"
                    style={{
                      transform: `translateX(-${((index + 0.5) / 3) * 100}%)`,
                    }}
                  />
                )}
                <span className="absolute bottom-1 start-1.5 rounded bg-black/40 px-1.5 py-0.5 text-[9px] text-white">
                  {name}
                </span>
                {index === (scene === 1 ? 1 : 0) && (
                  <span className="absolute end-2 top-2 size-1.5 rounded-full bg-[#a6a7ff]" />
                )}
              </div>
            ))}
          </div>
          <aside className="desktop-lesson-meeting-chat flex w-[29%] min-w-0 flex-col gap-2 rounded-lg bg-[var(--bg-secondary)] p-3">
            <p className="text-[11px] font-semibold">
              {t("desktopSetup.preview.meetingChat")}
            </p>
            <div className="min-h-0 flex-1 space-y-2 overflow-hidden text-[10px] leading-relaxed">
              <div>
                <span className="font-medium">
                  Priya{" "}
                  <span
                    className="ms-1 font-normal text-[var(--text-tertiary)]"
                    dir="ltr"
                  >
                    10:02
                  </span>
                </span>
                <p className="mt-1 rounded-lg bg-[var(--fill-1)] p-2">
                  {sample("import_line1")}
                </p>
              </div>
              <div>
                <span className="font-medium">Alex</span>
                <p className="mt-1 rounded-lg bg-[var(--fill-1)] p-2">
                  {sample("import_line2")}
                </p>
              </div>
            </div>
          </aside>
        </div>
        <div
          data-testid="desktop-demo-meeting-speech"
          className="flex shrink-0 items-center gap-2 px-4 pb-2 text-[11px] leading-snug"
        >
          <MicrophoneIcon size={12} className="shrink-0 text-[#6264a7]" />
          <span>
            <span className="font-semibold">
              {speakers[scene === 1 ? 1 : 0]}:{" "}
            </span>
            {sample(`import_line${scene === 1 ? 2 : 1}`)}
          </span>
        </div>
      </div>
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        <PreviewBubble
          mode={scene === 0 ? "idle" : "record"}
          finish={scene === 0 || scene === 2}
        />
      </div>
    </div>
  );
};

const DictationPreview = ({
  scene,
  shortcut,
  platform,
  sample,
}: {
  scene: number;
  shortcut: string;
  platform: PreviewPlatform;
  sample: DemoText;
}) => {
  const { t, i18n } = useTranslation();
  const spoken = sample("dictate_raw");
  const words = Array.from(
    new Intl.Segmenter(i18n.resolvedLanguage, { granularity: "word" }).segment(
      spoken,
    ),
    (item) => item.segment,
  );
  return (
    <div className="flex h-full flex-col">
      <WindowBar
        title={t("desktopSetup.preview.message")}
        platform={platform}
      />
      <div className="mx-auto flex w-full max-w-xl min-h-0 flex-1 flex-col px-6 pt-3 pb-28">
        <div className="glass-tile rounded-2xl p-3">
          <div className="mb-2 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            <FileTextIcon size={16} />
            {t("desktopSetup.preview.message")}
            <span className="ms-auto text-[10px] text-[var(--text-tertiary)]">
              {t("desktopSetup.preview.speechIllustration")}
            </span>
          </div>
          <div
            data-testid="desktop-demo-text-input"
            data-demo-target={scene === 0 ? "text-input" : undefined}
            className="relative grid min-h-12 rounded-xl bg-[var(--bg-secondary)] p-2.5 text-[13px] leading-relaxed"
          >
            {/* Overlaid text reserves both localized lengths, avoiding a layout jump. */}
            <span
              data-testid="desktop-demo-speech-draft"
              data-cleaning={scene === 2}
              className={`desktop-lesson-speech-draft col-start-1 row-start-1 rounded text-[var(--text-secondary)] ${scene === 0 || scene === 3 ? "invisible" : ""}`}
              dir="auto"
            >
              {words.map((word, index) => (
                <span
                  key={index}
                  className={
                    scene === 1 ? "desktop-lesson-spoken-word" : undefined
                  }
                  style={{
                    animationDelay: `${800 + (index / words.length) * 1700}ms`,
                  }}
                >
                  {word}
                </span>
              ))}
            </span>
            <span
              data-testid="desktop-demo-inserted-text"
              data-cleaning={scene === 2}
              className={`desktop-lesson-clean-text col-start-1 row-start-1 ${scene < 2 ? "invisible" : ""}`}
              dir="auto"
            >
              {sample("dictate_demoLine")}
            </span>
            {scene === 0 && (
              <span
                data-testid="desktop-demo-caret"
                className="desktop-lesson-caret absolute start-2.5 top-3 h-[1.2em] w-0.5 bg-[var(--accent)]"
              />
            )}
            {scene === 0 && <Pointer />}
          </div>
        </div>
      </div>
      <div
        data-testid="desktop-demo-shortcut-bubble"
        className="absolute bottom-4 right-4 flex flex-col items-end gap-3"
      >
        <div className="flex min-h-6 items-center justify-center gap-3">
          {scene === 1 || scene === 2 ? (
            <>
              <kbd
                data-demo-target="shortcut"
                className={`relative rounded-xl bg-[var(--bg-secondary)] px-3 py-2 text-[11px] font-semibold shadow-sm ${scene === 1 ? "desktop-lesson-key-double" : "desktop-lesson-key-single"}`}
              >
                {shortcut}
              </kbd>
            </>
          ) : scene === 3 ? (
            <span className="flex items-center gap-2 text-xs">
              <CheckIcon size={14} />
              {t("onboarding.ahaDemo.dictate_inserted")}
            </span>
          ) : (
            <span className="text-xs text-[var(--text-secondary)]">
              {t("desktopSetup.preview.stayInApp")}
            </span>
          )}
        </div>
        <PreviewBubble
          mode={
            scene === 0
              ? "idle"
              : scene === 3
                ? "complete"
                : scene === 2
                  ? "working"
                  : "dictate"
          }
        />
      </div>
    </div>
  );
};

export const DesktopWorkflowDemo = memo(
  ({
    kind,
    persona,
    shortcut,
    platform,
    onComplete,
  }: {
    kind: Workflow;
    persona: Persona | null;
    shortcut: string;
    platform: PreviewPlatform;
    onComplete?: () => void;
  }) => {
    const { t, i18n } = useTranslation();
    const sample: DemoText = (key) => {
      const base = `onboarding.ahaDemo.${key}`;
      const flavored = `${base}_${persona}`;
      return t(
        persona &&
          i18n.getResource(
            i18n.resolvedLanguage || i18n.language,
            "translation",
            flavored,
          )
          ? flavored
          : base,
      );
    };
    return (
      <div className="desktop-workflow-lesson mx-auto w-full max-w-3xl">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-[28px]">
          {t(`desktopSetup.story.${kind}Title`)}
        </h2>
        <p className="mb-4 mt-1.5 text-sm leading-relaxed text-[var(--text-secondary)]">
          {kind === "record" ? (
            <>
              <strong className="font-semibold text-[var(--text-primary)]">
                {t("desktopSetup.story.recordNoBot")}
              </strong>{" "}
            </>
          ) : null}
          {t(`desktopSetup.story.${kind}Body`, {
            shortcut,
            count: MAX_IMPORT_FILES,
          })}
        </p>
        <PreviewPlayer
          durations={workflowDurations[kind]}
          onComplete={onComplete}
          note={`${platform === "macos" ? "macOS" : "Windows"} · ${t("desktopSetup.preview.sample")}`}
          labels={[1, 2, 3, 4].map((step) =>
            t(`desktopSetup.story.${kind}${step}`, {
              shortcut,
              count: MAX_IMPORT_FILES,
            }),
          )}
        >
          {(scene) =>
            kind === "upload" ? (
              scene === 0 ? (
                <FilePickerPreview platform={platform} sample={sample} />
              ) : scene === 1 ? (
                <ProcessingPreview platform={platform} sample={sample} />
              ) : (
                <TranscriptPreview
                  sample={sample}
                  platform={platform}
                  download={scene === 2 ? "choose" : "saved"}
                />
              )
            ) : kind === "record" ? (
              scene === 3 ? (
                <TranscriptPreview
                  meeting
                  platform={platform}
                  sample={sample}
                />
              ) : (
                <MeetingPreview
                  scene={scene}
                  platform={platform}
                  sample={sample}
                />
              )
            ) : (
              <DictationPreview
                sample={sample}
                scene={scene}
                shortcut={shortcut}
                platform={platform}
              />
            )
          }
        </PreviewPlayer>
        <p className="sr-only">
          {sample(kind === "dictate" ? "dictate_demoLine" : "import_line1")}
        </p>
      </div>
    );
  },
);

const Toggle = ({
  on,
  platform,
}: {
  on: boolean;
  platform: PreviewPlatform;
}) => (
  <span
    data-platform={platform}
    className={`desktop-lesson-switch relative inline-block h-5 w-9 shrink-0 rounded-full transition-colors ${on ? (platform === "windows" ? "bg-[#0067c0]" : "bg-[#34c759]") : "bg-[var(--fill-3)]"}`}
    dir="ltr"
  >
    <span
      className={`absolute left-0 top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform ${on ? "translate-x-[18px]" : "translate-x-0.5"}`}
    />
  </span>
);

// Dismissible help on Home after setup; the tray remains the permanent entry point.
const ReturnToAppPreview = ({ platform }: { platform: PreviewPlatform }) => {
  const { t } = useTranslation();
  return (
    <div
      data-testid="desktop-permission-return"
      className="relative h-full p-5 pb-24"
    >
      <div className="h-full overflow-hidden rounded-2xl bg-[var(--bg-secondary)] shadow-md">
        <WindowBar
          title={
            platform === "macos"
              ? t("desktopDictation.onboarding.preview.macos.systemSettings")
              : t("desktopDictation.onboarding.preview.windows.settings")
          }
          platform={platform}
        />
        <div className="flex h-[calc(100%-36px)] items-center justify-center text-[var(--text-tertiary)]">
          <SettingsIcon size={36} />
        </div>
      </div>
      <div className="desktop-lesson-return-app absolute inset-x-5 top-5 bottom-24 overflow-hidden rounded-2xl bg-[var(--bg-secondary)] shadow-md">
        <WindowBar title="1Transcribe" platform={platform} />
        <div className="flex h-[calc(100%-36px)] flex-col items-center justify-center gap-3 px-5 text-center">
          <DictationBubbleIcon size={40} />
          <span className="text-sm font-semibold">
            {t("desktopSetup.guide.returnToApp")}
          </span>
        </div>
      </div>
      <div
        data-testid="desktop-permission-app-switcher"
        data-platform={platform}
        dir="ltr"
        className={`absolute bottom-3 flex h-16 items-center justify-center gap-5 bg-[var(--bg-secondary)] px-5 shadow-lg ${platform === "macos" ? "left-1/2 -translate-x-1/2 rounded-2xl" : "inset-x-0"}`}
      >
        {platform === "windows" ? (
          <span className="text-2xl text-[#0067c0]">⊞</span>
        ) : (
          <FileTextIcon size={25} className="text-[var(--text-secondary)]" />
        )}
        <span data-demo-target="return-to-app" className="relative rounded-xl">
          <img src="/logo.png" alt="" className="size-11 rounded-xl" />
          <Pointer />
        </span>
        <SettingsIcon size={25} className="text-[var(--text-secondary)]" />
      </div>
    </div>
  );
};

export const DesktopPermissionDemo = memo(
  ({
    kind,
    platform,
    prompt,
  }: {
    kind: Permission;
    platform: "macos" | "windows";
    prompt: boolean;
  }) => {
    const { t } = useTranslation();
    const windows = platform === "windows";
    const localAudio = windows && kind === "systemAudio";
    const pane = t(
      kind === "systemAudio"
        ? "desktopSetup.systemAudioPane"
        : `desktopDictation.onboarding.preview.macos.${kind}`,
    );
    const guide = localAudio
      ? "windowsAudio"
      : prompt
        ? "micPrompt"
        : windows
          ? "windowsMic"
          : kind === "systemAudio"
            ? "macAudio"
            : "macSettings";
    const labels = [
      ...[1, 2, 3].map((step) =>
        t(`desktopSetup.guide.${guide}${step}`, { pane }),
      ),
      t("desktopSetup.guide.returnToApp"),
    ];
    return (
      <div
        data-testid={`desktop-permission-preview-${kind}`}
        data-platform={platform}
      >
        <PreviewPlayer
          labels={labels}
          sceneDurationMs={2600}
          note={t("desktopSetup.guide.illustration")}
        >
          {(scene) =>
            scene === 3 ? (
              <ReturnToAppPreview platform={platform} />
            ) : localAudio ? (
              <div className="flex h-full flex-col items-center justify-center gap-5 p-6">
                <div className="flex items-center gap-3">
                  <span className="glass-tile flex flex-col items-center gap-2 rounded-2xl p-4 text-xs">
                    <MicrophoneIcon size={24} />
                    {t("desktopSetup.story.microphoneReason")}
                  </span>
                  <span className="text-[var(--text-tertiary)]">+</span>
                  <span className="glass-tile flex flex-col items-center gap-2 rounded-2xl p-4 text-xs">
                    <AudioWaveformIcon size={24} />
                    {t("desktopSetup.story.computerAudio")}
                  </span>
                </div>
                <p className="max-w-xs text-center text-sm leading-relaxed">
                  {t(`desktopSetup.guide.windowsAudioDetail${scene + 1}`)}
                </p>
                {scene === 1 && <AudioMeter />}
              </div>
            ) : prompt ? (
              <div className="flex h-full items-center justify-center bg-[var(--ambience-blue)]/25 p-5">
                <div className="w-full max-w-xs rounded-[22px] bg-[var(--bg-secondary)] p-4 text-center shadow-xl">
                  <img
                    src="/logo.png"
                    alt=""
                    className="mx-auto size-8 rounded-lg"
                  />
                  <p className="mt-3 text-sm font-semibold">
                    {t("desktopDictation.onboarding.preview.macos.promptTitle")}
                  </p>
                  <p className="mt-2 text-[11px] text-[var(--text-secondary)]">
                    {t("desktopDictation.onboarding.preview.macos.promptBody")}
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-medium">
                    <span className="rounded-lg bg-[var(--fill-2)] p-2.5">
                      {t("desktopDictation.onboarding.preview.macos.dontAllow")}
                    </span>
                    <span
                      data-demo-target={
                        scene === 1 ? "allow-microphone" : undefined
                      }
                      className="relative rounded-lg bg-[#147efb] p-2.5 text-white"
                    >
                      {t("desktopDictation.onboarding.preview.macos.allow")}
                      {scene === 1 && <Pointer />}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col">
                <WindowBar
                  platform={platform}
                  title={t(
                    `desktopDictation.onboarding.preview.${windows ? "windows.settings" : "macos.systemSettings"}`,
                  )}
                />
                <div className="flex min-h-0 flex-1">
                  <div className="flex-1 px-5 py-5">
                    <p className="flex items-center gap-2 text-[11px] text-[var(--text-tertiary)]">
                      <SettingsIcon size={14} />
                      {t("desktopDictation.onboarding.preview.macos.privacy")}
                    </p>
                    <h2 className="mt-2 text-base font-semibold leading-snug">
                      {pane}
                    </h2>
                    <div className="mt-3 space-y-2">
                      {(windows
                        ? [
                            t(
                              "desktopDictation.onboarding.preview.windows.microphoneAccess",
                            ),
                            t(
                              "desktopDictation.onboarding.preview.windows.desktopApps",
                            ),
                          ]
                        : ["1Transcribe"]
                      ).map((label, index) => (
                        <div
                          key={label}
                          className="relative flex min-h-12 items-center gap-2 rounded-xl bg-[var(--fill-1)] px-3 py-2"
                        >
                          {!windows && (
                            <img
                              src="/logo.png"
                              alt=""
                              className="size-7 rounded-lg"
                            />
                          )}
                          <span className="flex-1 text-xs">{label}</span>
                          <span
                            data-demo-target={
                              scene === index + 1
                                ? "permission-toggle"
                                : undefined
                            }
                            className="relative flex rounded-full"
                          >
                            <Toggle on={scene > index} platform={platform} />
                            {scene === index + 1 && <Pointer />}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          }
        </PreviewPlayer>
      </div>
    );
  },
);
